/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';

import { useRoom } from '../../../hooks/useRoom';
import { participantAPI } from '../../../api/participant';
import { useVideoContext } from '../../../contexts/videoProvider/videoContext';
import { LOCAL_TRACKS_LENGTH } from '../../../contexts/videoProvider/hooks/useLocalTracks';
import { setCurrentUser } from '../../../actions/userAction';
import { getVideoVendorApiKey } from '@utils/videoVendorStorageUtils';

export const useConnectToVideoProvider = () => {
  const dispatch = useDispatch();
  const { roomId } = useRoom();
  const history = useHistory();
  const { connect: connectToVideoProvider, localTracks } = useVideoContext();

  const [apiKey, setAPIKey] = useState(getVideoVendorApiKey(roomId));

  const refreshAPIKey = async () => {
    const session = await participantAPI.sessions.fetchSession();
    if (session.videoAccessToken) {
      const users = await participantAPI.calls.fetchCallUserData(session.callId);
      const currentUser = users.inVideoCall.find(user => user.id === session.id);
      const primaryUser = currentUser?.users.find(user => !user.primaryParticipantId);

      if (primaryUser) {
        // setting the current participant in the store
        setCurrentUser({
          id: currentUser!.id,
          firstName: primaryUser.firstName,
          lastName: primaryUser.lastName!,
          permissions: [],
          address: null,
          principalId: '',
          email: '',
        })(dispatch);
      }

      setAPIKey(session.videoAccessToken);
    } else {
      history.push(`/rooms/${roomId}/`);
    }
  };

  useEffect(() => {
    refreshAPIKey();
  }, [roomId]);

  useEffect(() => {
    // checking for local track length to make sure getting local video/audio track
    // has finished before connecting to the room
    if (apiKey && localTracks.length === LOCAL_TRACKS_LENGTH) {
      connectToVideoProvider(apiKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, localTracks.length]);

  return { apiKey };
};
